import { css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import './d-custom-slide';
import './d-news-slide-fhi';
import './d-news-slide-nrk';
import './d-weather-slide';
import './d-employees-slide';
import 'src/library/elements/d-loading-image';
import type { RssNewsItem, Slide } from 'src/content/screens/slide-element';
import type { LibraryItem } from 'src/library/screen/library-item';
import type { EmployeePresenceStatus } from 'src/store/selectors/staffing/employee-presence-status';
import { resolveAssetUrl } from 'src/content/screens/resolve-asset-url';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import { ResponsiveElement } from 'src/library/elements/responsive-container';

@customElement('d-slideshow')
export class DSlideshow extends ResponsiveElement {
  static readonly styles = css`
    :host {
      display: block;
      box-sizing: border-box;
      aspect-ratio: 16/9;
    }
    .main {
      position: relative;
      aspect-ratio: 16/9;
      border: 1px solid silver;
      border-radius: 12px;
      overflow: hidden;
    }
    .main > * {
      position: absolute;
      width: 100%;
    }
    img {
      width: 100%;
    }
    /* d-loading-image er layout-agnostisk: host får bredde fra .main > *,
       og det indre bildet styles via ::part(img) slik det raa img-elementet var. */
    d-loading-image::part(img) {
      width: 100%;
    }
    .buttons-wrapper {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }
    .buttons {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 56px;
      width: 162px;
      border-radius: 28px;
      background: hsla(0, 0%, 0%, 0.36);
    }
    .button {
      all: unset;
      width: 36px;
      height: 36px;
      background-repeat: no-repeat;
      background-position: 50% 50%;
      background-size: contain;
      transition: transform 0.2s;
      cursor: pointer;
    }
    .button.rewind {
      background-image: url(/images/slideshow-rewind.svg);
    }
    .button.play {
      background-image: url(/images/slideshow-play.svg);
    }
    :host([playing]) .button.play {
      display: none;
    }
    .button.pause {
      background-image: url(/images/slideshow-pause.svg);
      display: none;
    }
    :host([playing]) .button.pause {
      display: block;
    }
    .button.fast-forward {
      background-image: url(/images/slideshow-fast-forward.svg);
    }
    .slider {
      bottom: 0;
      box-sizing: border-box;
      width: 100%;
      background: linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
      padding: 40px 10px 6px 10px;
      z-index: 1;
    }
    #timeline {
      display: flex;
      align-items: flex-end;
      width: 100%;
      filter: drop-shadow(0 0 10px hsla(1, 0%, 0%, 0.5));
    }
    #timeline > div {
      position: relative;
      box-sizing: border-box;
      border: 1px solid transparent;
      border-radius: 4px;
      overflow: hidden;
      z-index: 1;
      cursor: pointer;
    }
    #timeline > *.selected {
      border-color: white;
    }
    #timeline > div:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }
    #timeline > div > * {
      transform-origin: top;
    }
    input[type='range'] {
      width: 100%;
      margin: 5px 0;
      background-color: transparent;
      -webkit-appearance: none;
      cursor: pointer;
    }
    input[type='range']:focus {
      outline: none;
    }
    input[type='range']::-webkit-slider-runnable-track {
      width: 100%;
      height: 4px;
      margin: 0 -5px;
      border: 0;
      background: transparent;
      cursor: pointer;
    }
    input[type='range']::-webkit-slider-thumb {
      margin-top: -7px;
      width: 18px;
      height: 18px;
      background: #ffffff;
      border: none;
      border-radius: 9px;
      cursor: pointer;
      -webkit-appearance: none;
      box-shadow: 0 0 10px hsla(1, 0%, 0%, 0.5);
    }
    input[type='range']:focus::-webkit-slider-runnable-track {
      background: transparent;
    }
    input[type='range']::-moz-range-track {
      background: transparent;
      border: 0;
      border-radius: 3px;
      width: 100%;
      height: 6px;
      cursor: pointer;
    }
    input[type='range']::-moz-range-thumb {
      width: 18px;
      height: 18px;
      background: #ffffff;
      border: none;
      border-radius: 9px;
      cursor: pointer;
    }
    @media (hover: hover) {
      .buttons-wrapper,
      .slider {
        opacity: 0;
        transition: opacity 0.3s;
      }
      .main:hover .buttons-wrapper,
      .main:hover .slider {
        opacity: 1;
      }
      .button:hover {
        transform: scale(1.2);
      }
    }
  `;
  @property({ type: Array })
  playlist: Slide[] = [];
  @property({ type: Array })
  library: LibraryItem[] = [];
  @property({ type: Number })
  transition = 0;
  @property({ type: String })
  assetBase = '';
  @property({ type: Array })
  newsFeed: RssNewsItem[] = [];
  @property({ type: Array })
  newsFeedFhi: RssNewsItem[] = [];
  @property({ type: Array })
  employeesPresenceStatus: EmployeePresenceStatus[] = [];
  @state()
  private seconds = 0;
  @state()
  private speed = 1;
  @property({ type: Boolean, reflect: true })
  playing = false;
  @query('#timeline')
  timelineElm!: HTMLDivElement;
  private timelineHeight = 32;
  private timelineGap = 2;
  private timelineWidth = 0;
  private timerId?: number;

  private get timelineNetWidth(): number {
    const gaps = (this.playlist.length - 1) * this.timelineGap;
    return this.timelineWidth - gaps;
  }

  onResize(_contentRect: DOMRectReadOnly) {
    super.onResize(_contentRect);
    this.timelineWidth = this.timelineElm.offsetWidth;
  }

  private step() {
    this.seconds += 0.1 * this.speed;
    if (this.seconds > this.totalDuration) {
      this.seconds = 0;
    }
  }

  private startTimer() {
    this.playing = true;
    this.timerId = window.setInterval(() => {
      this.step();
    }, 100);
  }

  private stopTimer() {
    this.playing = false;
    clearInterval(this.timerId);
  }

  rewind() {
    this.seconds = 0;
  }

  playNormalSpeed() {
    this.stopTimer();
    this.speed = 1;
    this.startTimer();
  }

  fastForward() {
    this.stopTimer();
    if (this.speed === 1) {
      this.speed = 2;
    } else if (this.speed === 2) {
      this.speed = 4;
    }
    this.startTimer();
  }

  connectedCallback() {
    super.connectedCallback();
    this.playNormalSpeed();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopTimer();
  }

  private get durations() {
    return [...this.playlist].map((slide) => slide.duration);
  }

  private get positions(): { start: number; end: number }[] {
    const result: { start: number; end: number }[] = [];
    let start = 0;
    this.playlist.forEach((slide) => {
      result.push({ start, end: start + slide.duration });
      start = start + slide.duration;
    });
    return result;
  }

  private get totalDuration() {
    return this.durations.reduce((accumulator, current) => accumulator + current, 0);
  }

  private get currentSlideIndex(): number {
    let result = 0;
    this.positions.forEach((positions, index: number) => {
      if (positions.start <= this.seconds && positions.end >= this.seconds) {
        result = index;
      }
    });
    return result;
  }

  private get nextSlideIndex(): number {
    if (this.currentSlideIndex + 1 > this.playlist.length - 1) {
      return 0;
    }
    return this.currentSlideIndex + 1;
  }

  private get currentSlide(): Slide {
    return this.playlist[this.currentSlideIndex];
  }

  private get nextSlide(): Slide {
    return this.playlist[this.nextSlideIndex];
  }

  onSelectSlide(index: number, behavior: undefined | 'stop' = undefined) {
    if (behavior === 'stop') {
      this.stopTimer();
    }
    // d-screen-view.onPlaylistChanged kaller findIndex(slideId) på den nye
    // playlisten og videresender resultatet hit. Hvis slide-en som ble klikket
    // ikke finnes lenger (slettet i samme event, eller stale id), returnerer
    // findIndex -1 og positions[-1] er undefined → krasj. Ignorér ugyldige
    // indices i stedet for å kaste.
    const position = this.positions[index];
    if (!position) return;
    this.seconds = position.start;
  }

  private onSliderChanged(e) {
    this.stopTimer();
    this.seconds = Number(e.target.value);
  }

  private get currentSlideStyle() {
    let opacity = 1;
    const currentPosition = this.positions[this.currentSlideIndex];
    const left = currentPosition.end - this.seconds;
    if (left < this.transition / 2) {
      opacity = left * this.transition * 2;
    }
    return {
      opacity: opacity + '',
      zIndex: '1',
    };
  }

  private get thumbnailWrapperStyle() {
    return {
      height: this.timelineHeight + 'px',
    };
  }

  private thumbnailWrapperClass(index: number) {
    return {
      selected: index === this.currentSlideIndex,
    };
  }

  private thumbnailStyle(slide: Slide) {
    const thumbnailWidthPercent = (slide.duration / this.totalDuration) * 100;
    const thumbnailWidthPixels = (this.timelineNetWidth * thumbnailWidthPercent) / 100;
    const thumbnailHeightPixels = (thumbnailWidthPixels / 16) * 9;
    const scale = (this.timelineHeight / thumbnailHeightPixels) * 100;
    return {
      width: thumbnailWidthPixels + 'px',
      transform: 'scaleY(' + scale + '%)',
    };
  }

  private get timelineStyle() {
    return {
      gap: this.timelineGap + 'px',
    };
  }

  private get rangeStyle() {
    const position = Math.round((this.seconds / this.totalDuration) * 100);
    const background = 'linear-gradient(90deg, var(--themeColor) ' + position + '%, white ' + position + '%)';
    return {
      background,
    };
  }

  /** Library-thumbnailen (base64) for et media, hvis den finnes — blur-up. */
  private thumbnailFor(media: string): string {
    if (!media) return '';
    const item = this.library.find((i) => i.assets?.includes(media));
    return item?.thumbnail ?? '';
  }

  renderSlide(slide: Slide) {
    const url = resolveAssetUrl(slide.media, this.assetBase);
    switch (slide.type) {
      case 'custom':
        return html` <d-custom-slide .slide=${slide} .assetBase=${this.assetBase}></d-custom-slide> `;
      case 'fhi':
        return html` <d-news-slide-fhi .slide=${slide} .feed=${this.newsFeedFhi}></d-news-slide-fhi> `;
      case 'news':
        return html` <d-news-slide-nrk .slide=${slide} .feed=${this.newsFeed}></d-news-slide-nrk> `;
      case 'weather':
        return html` <d-weather-slide .slide=${slide} .assetBase=${this.assetBase}></d-weather-slide> `;
      case 'employees':
        return html`
          <d-employees-slide .slide=${slide} .employees=${this.employeesPresenceStatus}></d-employees-slide>
        `;
      case 'image':
        // d-loading-image venter på at bildet er ferdig dekodet før det vises
        // (decode-før-bytt), så vi unngår at et stort bilde males halvferdig
        // over en treg strøm. Uten asset-path lastes det umiddelbart (som det
        // rå <img> gjorde før). Library-thumbnailen brukes som blur-up.
        return html` <d-loading-image src="${url}" placeholder="${this.thumbnailFor(slide.media)}"></d-loading-image>`;
      default:
        return nothing;
    }
  }

  renderThumbnail(slide: Slide) {
    const url = resolveAssetUrl(slide.media, this.assetBase);
    switch (slide.type) {
      case 'custom':
        return html`
          <d-custom-slide
            .slide=${slide}
            .assetBase=${this.assetBase}
            style=${styleMap(this.thumbnailStyle(slide))}
          ></d-custom-slide>
        `;
      case 'fhi':
        return html`
          <d-news-slide-fhi
            .slide=${slide}
            .feed=${this.newsFeedFhi}
            style=${styleMap(this.thumbnailStyle(slide))}
          ></d-news-slide-fhi>
        `;
      case 'news':
        return html`
          <d-news-slide-nrk
            .slide=${slide}
            .feed=${this.newsFeed}
            style=${styleMap(this.thumbnailStyle(slide))}
          ></d-news-slide-nrk>
        `;
      case 'weather':
        return html`
          <d-weather-slide
            .slide=${slide}
            .assetBase=${this.assetBase}
            style=${styleMap(this.thumbnailStyle(slide))}
          ></d-weather-slide>
        `;
      case 'employees':
        return html`
          <d-employees-slide
            .slide=${slide}
            .employees=${this.employeesPresenceStatus}
            style=${styleMap(this.thumbnailStyle(slide))}
          ></d-employees-slide>
        `;
      case 'image':
        return html` <img
          src="${url}"
          style=${styleMap(this.thumbnailStyle(slide))}
          loading="lazy"
          decoding="async"
        />`;
      default:
        return nothing;
    }
  }

  renderControls() {
    return html`
      <div class="buttons-wrapper">
        <div class="buttons">
          <button class="button rewind" @click=${() => this.rewind()}></button>
          <button class="button play" @click=${() => this.playNormalSpeed()}></button>
          <button class="button pause" @click=${() => this.stopTimer()}></button>
          <button class="button fast-forward" @click=${() => this.fastForward()}></button>
        </div>
      </div>
      <div class="slider">
        <div id="timeline" style=${styleMap(this.timelineStyle)}>
          ${this.playlist.map((slide, index) => {
            return html` <div
              class=${classMap(this.thumbnailWrapperClass(index))}
              style=${styleMap(this.thumbnailWrapperStyle)}
              @click=${() => this.onSelectSlide(index, 'stop')}
            >
              ${this.renderThumbnail(slide)}
            </div>`;
          })}
        </div>
        <input
          type="range"
          style=${styleMap(this.rangeStyle)}
          min="0"
          max="${this.totalDuration}"
          step="0.01"
          .value=${this.seconds + ''}
          @input=${(e) => this.onSliderChanged(e)}
        />
      </div>
    `;
  }

  render() {
    return html`
      <div class="main">
        <div>${this.renderSlide(this.nextSlide)}</div>
        <div style=${styleMap(this.currentSlideStyle)}>${this.renderSlide(this.currentSlide)}</div>
        ${this.renderControls()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-slideshow': DSlideshow;
  }
}
